import { useEffect } from 'react';

import { ApiQueryBasePromise } from '@/types';
import { ApiDomain } from '@nf/types/site';
import { getResponseJwtHeader } from '@/utils/web';
import { useQuery } from '@tanstack/react-query';

import { fetchComposer } from '@nf/utils-common/compose-fetch';
import queryKeys from '@/constants/query-keys';
import { useSiteStore } from '@/store/site';
import { getAuthToken, useAuthActions } from '@/store/auth';
import { cloudUrlParser } from '@/utils/aws-domain-util';

export interface Message {
	content: string;
	posttime: string;
}

interface GetMessages {
	Data: Message[];
}

interface GetTopMessage {
	Data: Message
}

export interface isNewMessages {
	showPublicMsg: boolean;
	showSpecialMsg: boolean;
	showPrivateMsg: boolean;
	showPrivateCount: number;
	ShowMessageOn: boolean;
}

interface GetCheckMessages {
	Data: isNewMessages;
}

export const fetchMessages = async (key: string): Promise<ApiQueryBasePromise<GetMessages>> => {
	const apiDomain = useSiteStore.getState().apiDomain.ctcdDomain;
	const formData = new FormData();
	const dataEntries = {
		tab: key,
		dtType24: 'true'
	};

	Object.entries(dataEntries).forEach(([datakey, datavalue]) => {
		formData.append(datakey, datavalue);
	})

	const authToken = getAuthToken();
	const response = await fetchComposer.postWithBearerBase(cloudUrlParser(`${apiDomain}/TopMessage/GetMessage`), { body: formData })(authToken);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}
	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
};

export const useMessages = (key: string) => {
	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [queryKeys.MESSAGES_DATA, key],
		queryFn: () => fetchMessages(key),
		cacheTime: 0,
		refetchOnWindowFocus: false,
		enabled: true,
	});

	return {
		JwtToken: data?.jwtToken,
		messagesInfo: data?.data.Data || [],
		isLoading,
		isError,
		error,
		refetch
	};
};


export const fetchCheckMessages = async (apiDomain:string|undefined): Promise<ApiQueryBasePromise<GetCheckMessages>> => {
	const authToken = getAuthToken();
	const response = await fetchComposer.postWithBearerBase(cloudUrlParser(`${apiDomain}/TopMessage/CheckHasMessage`), { body: null })(authToken);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
};

export const useCheckMessages = () => {
	const apiDomain = useSiteStore(state => state.apiDomain);

	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [queryKeys.MESSAGES_CHECKDATA, apiDomain.ctcdDomain],
		queryFn: () => fetchCheckMessages(apiDomain.ctcdDomain),
		cacheTime: 0,
		refetchOnWindowFocus: false,
		enabled: true
	});
	const { updateAuthToken } = useAuthActions();

	useEffect(() => {
		data?.jwtToken && updateAuthToken(data.jwtToken);
	}, [data?.jwtToken]);

	return {
		messagesInfo: data?.data.Data,
		isLoading,
		isError,
		error,
		refetch
	};
};

const fetchTopMessage = async (): Promise<ApiQueryBasePromise<GetTopMessage>> => {
	const apiDomain = useSiteStore.getState().apiDomain.ctcdDomain;
	const authToken = getAuthToken();
	const response = await fetchComposer.postWithBearerBase(cloudUrlParser(`${apiDomain}/TopMessage/GetTopMessage`), { body: null })(authToken);
	if (!response.ok) {
		throw new Error(`${response.status}: ${response.statusText}`);
	}

	return {
		data: await response.json(),
		jwtToken: getResponseJwtHeader(response)
	};
};
export const useTopMessage = () => {
	const { data, isLoading, isError, error, refetch } = useQuery({
		queryKey: [queryKeys.MESSAGE_TOP, 'TOP'],
		queryFn: () => fetchTopMessage(),
		cacheTime: 0,
		refetchOnWindowFocus: false,
		enabled: true
	});
	const { updateAuthToken } = useAuthActions();

	useEffect(() => {
		data?.jwtToken && updateAuthToken(data.jwtToken);
	}, [data?.jwtToken]);

	return {
		messagesInfo: data?.data.Data,
		isLoading,
		isError,
		error,
		refetch
	};
};